'use client'

import useFormatDate from '@/src/hooks/date/useFormatDate'
import type { HomeAISearchQuery } from '@/src/types/homeAISearch'
import { convertToUSDateFormat } from '@/src/utils/date/convertDateFormat'
import { isFlyin } from '@/src/utils/generalUtils/isFlyin'
import type { HomeAIMessages } from '@/src/utils/homeAISearch/locale'
import { Box, Card, LinearProgress, Typography, useTheme } from '@mui/material'
import dayjs from 'dayjs'
import { Armchair, Calendar, Plane, Users } from 'lucide-react'
import { useLocale, useTranslations } from 'next-intl'
import { useEffect, useRef } from 'react'

export interface HomeAIPendingTripCardProps {
    searchQuery: HomeAISearchQuery
    filterQuery?: string
    messages: HomeAIMessages
    /** How long the progress bar takes to fill before auto-confirming. */
    autoConfirmMs?: number
    /** Fired when the progress bar completes — navigates straight to the SRP (no LLM call). */
    onAutoConfirm: () => void
}

/** `origin` / `destination` are plain city text from the API (UI locale); `from` / `to` are IATA. */
function cityWithIataCode (city: string, iata: string): string {
    const c = city.trim()
    return c ? `${c} (${iata})` : iata
}

function formatPassengerBreakdown (
    adults: number,
    children: number,
    infants: number,
    p: HomeAIMessages['pendingTripPassengers']
): string {
    const parts: string[] = []
    if (adults > 0) {
        parts.push(adults === 1 ? p.adultsOne : p.adultsMany.replace('{count}', String(adults)))
    }
    if (children > 0) {
        parts.push(children === 1 ? p.childOne : p.childrenMany.replace('{count}', String(children)))
    }
    if (infants > 0) {
        parts.push(infants === 1 ? p.infantOne : p.infantsMany.replace('{count}', String(infants)))
    }
    return parts.join(p.joiner)
}

function buildDateRangeLabel (
    departDDMMYYYY: string,
    returnDDMMYYYY: string | undefined,
    formatDate: (d: string, f: string) => string
): string {
    const depUS = convertToUSDateFormat(departDDMMYYYY)
    if (!returnDDMMYYYY?.trim()) {
        return formatDate(depUS, 'DD MMM, YYYY')
    }
    const retUS = convertToUSDateFormat(returnDDMMYYYY)
    const d1 = dayjs(depUS)
    const d2 = dayjs(retUS)
    if (d1.isValid() && d2.isValid() && d1.year() !== d2.year()) {
        return `${formatDate(depUS, 'DD MMM, YYYY')} – ${formatDate(retUS, 'DD MMM, YYYY')}`
    }
    return `${formatDate(depUS, 'DD MMM')} – ${formatDate(retUS, 'DD MMM, YYYY')}`
}

export function HomeAIPendingTripCard ({
    searchQuery,
    filterQuery,
    messages,
    autoConfirmMs = 7000,
    onAutoConfirm
}: HomeAIPendingTripCardProps) {
    const theme = useTheme()
    const uiLocale = useLocale()
    const tCommon = useTranslations('common')
    const { formatDate } = useFormatDate()

    // Auto-confirm: when the progress bar finishes filling, navigate straight to the SRP.
    // The bar itself is animated purely in CSS (see LinearProgress below) so it runs on the
    // compositor at 60fps with zero re-renders; JS only owns the action via this one timeout.
    // Cleared on unmount — and the card unmounts the moment a new query starts loading — so an
    // in-flight LLM query cancels the countdown. Single timer, cleared in every path: no leak.
    const onAutoConfirmRef = useRef(onAutoConfirm)
    onAutoConfirmRef.current = onAutoConfirm

    useEffect(() => {
        const timer = setTimeout(() => onAutoConfirmRef.current(), autoConfirmMs)
        return () => clearTimeout(timer)
    }, [autoConfirmMs])

    const adults = Number(searchQuery.adults) || 0
    const childs = Number(searchQuery.childs) || 0
    const infants = Number(searchQuery.infants) || 0
    const totalTraveler = adults + childs + infants
    const isSinglePerson = totalTraveler < 2

    const tripType = searchQuery.return_date?.trim() ? tCommon('roundTrip') : tCommon('oneWay')
    const travelClass = searchQuery.class ? tCommon(searchQuery.class as any) : ''

    const dateRangeLabel = buildDateRangeLabel(searchQuery.depart_date, searchQuery.return_date, formatDate)

    const travellerLabel = tCommon('traveller', { s: isSinglePerson ? '' : 's' })

    const fromLine = cityWithIataCode(searchQuery.origin, searchQuery.from)
    const toLine = cityWithIataCode(searchQuery.destination, searchQuery.to)
    const passengerDetail = formatPassengerBreakdown(adults, childs, infants, messages.pendingTripPassengers)
    const preferenceTrimmed = filterQuery?.trim() ?? ''
    const showDetailsRow = Boolean(passengerDetail || preferenceTrimmed)

    const iconSm = { width: 14, height: 14, flexShrink: 0 }
    const iconPlaneStyle = { width: 16, height: 16, flexShrink: 0, color: theme.palette.primary.main }

    return (
        <Box
            sx={{
                maxWidth: 448,
                width: '100%',
                my: 1.5
            }}
        >
            <Card
                sx={{
                    position: 'relative',
                    borderRadius: 3,
                    bgcolor: 'background.paper',
                    width: '100%',
                    overflow: 'hidden'
                }}
            >
                <Box sx={{ p: 2, }}>
                    {/* Header: trip type + cabin */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            mb: 1,
                        }}
                    >
                        <Typography
                            component="span"
                            sx={{
                                fontSize: 12,
                                fontWeight: 500,
                                color: 'text.secondary',
                                letterSpacing: uiLocale === 'ar' ? 'normal' : '0.06em',
                                textTransform: uiLocale === 'ar' ? 'none' : 'uppercase'
                            }}
                        >
                            {tripType}
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                fontSize: 12,
                                color: 'text.secondary'
                            }}
                        >
                            <Armchair style={iconSm} aria-hidden />
                            <span>{travelClass}</span>
                        </Box>
                    </Box>

                    {/* Route */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flexWrap: 'wrap',
                            mb: 1,
                        }}
                    >
                        <Typography
                            component="span"
                            sx={{
                                fontSize: '1.125rem',
                                fontWeight: 700,
                                color: 'text.primary',
                                lineHeight: 1.3
                            }}
                        >
                            {fromLine}
                        </Typography>
                        <Plane style={iconPlaneStyle} aria-hidden />
                        <Typography
                            component="span"
                            sx={{
                                fontSize: '1.125rem',
                                fontWeight: 700,
                                color: 'text.primary',
                                lineHeight: 1.3
                            }}
                        >
                            {toLine}
                        </Typography>
                    </Box>

                    {/* Date + travellers (one row) */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 2,
                            mb: 1,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                fontSize: 14,
                                color: 'text.secondary'
                            }}
                        >
                            <Calendar style={iconSm} aria-hidden />
                            <span>{dateRangeLabel}</span>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                fontSize: 14,
                                color: 'text.secondary'
                            }}
                        >
                            <Users style={iconSm} aria-hidden />
                            <span>
                                {totalTraveler} {travellerLabel}
                            </span>
                        </Box>
                    </Box>

                    {/* Passenger breakdown | AI preferences */}
                    {showDetailsRow ? (
                        <Typography
                            component="div"
                            variant="caption"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: 0.75,
                                color: 'text.secondary',
                                fontSize: 12,
                                // mb: 1
                            }}
                        >
                            {(passengerDetail && totalTraveler > 1) ? <span>{passengerDetail}</span> : null}
                            {passengerDetail && preferenceTrimmed ? (
                                <Box
                                    component="span"
                                    sx={{
                                        color: 'divider',
                                        userSelect: 'none',
                                        lineHeight: 1,
                                        mx: 0.25
                                    }}
                                >
                                    {messages.pendingTripPreferenceSeparator}
                                </Box>
                            ) : null}
                            {preferenceTrimmed ? <span>{preferenceTrimmed}</span> : null}
                        </Typography>
                    ) : null}

                </Box>

                {/* Auto-confirm countdown along the bottom edge. value stays at 0 (empty) and the
                    bar's transform is animated by CSS from empty → full over autoConfirmMs — runs on
                    the compositor (smooth, no React renders). The setTimeout above fires the navigation. */}
                <LinearProgress
                    variant="determinate"
                    value={0}
                    aria-hidden
                    color={isFlyin() ? 'primary' : 'secondary'}
                    sx={{
                        '& .MuiLinearProgress-bar': {
                            transition: 'none',
                            animation: `aiAutoConfirmProgress ${autoConfirmMs}ms linear forwards`,
                            '@keyframes aiAutoConfirmProgress': {
                                from: { transform: 'translateX(-100%)' },
                                to: { transform: 'translateX(0)' }
                            }
                        }
                    }}
                />
            </Card>
        </Box>
    )
}
